import { Link, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from './lib/auth'
import {
  IconNodes, IconTasks, IconClients, IconSites, IconDatabase, IconDBUsers,
  IconFTP, IconCron, IconKey, IconDocs, IconLogout, IconExternal, IconCert, IconFile, IconIP,
} from './components/icons'

import type { ReactElement } from 'react'
type NavItem = { to: string; label: string; icon: (p: any) => ReactElement; disabled?: boolean; external?: string }

const nav: { section: string; items: NavItem[] }[] = [
  { section: 'Обзор', items: [
    { to: '/',      label: 'Главная', icon: IconNodes },
  ]},
  { section: 'Инфраструктура', items: [
    { to: '/nodes',        label: 'Ноды',      icon: IconNodes },
    { to: '/ip-addresses', label: 'IP-адреса', icon: IconIP },
    { to: '/firewall',     label: 'Firewall',   icon: IconIP },
    { to: '/tasks',        label: 'Задачи',    icon: IconTasks },
  ]},
  { section: 'Хостинг', items: [
    { to: '/clients',   label: 'Клиенты',          icon: IconClients },
    { to: '/sites',        label: 'Сайты',       icon: IconSites },
    { to: '/certificates', label: 'SSL-сертификаты', icon: IconCert },
    { to: '/databases',    label: 'Базы данных',  icon: IconDatabase },
    { to: '/db-users',  label: 'Пользователи БД',  icon: IconDBUsers },
    { to: '/ftp',       label: 'FTP',              icon: IconFTP },
    { to: '/files',     label: 'Файлы',           icon: IconFile },
    { to: '/cron',      label: 'Cron',             icon: IconCron },
  ]},
  { section: 'Система', items: [
    { to: '/settings', label: 'Настройки',           icon: IconCron },
    { to: '/account',  label: 'Настройки аккаунта', icon: IconClients },
    { to: '/api-keys', label: 'API-ключи',         icon: IconKey },
    { to: '/docs',     label: 'API-документация',  icon: IconDocs, external: '/api/docs' },
  ]},
]

export function App() {
  const { user, logout } = useAuth()
  const loc = useLocation()
  const [open, setOpen] = useState(false)

  // Закрывать меню при навигации
  useEffect(() => { setOpen(false) }, [loc.pathname])

  const currentTitle = (() => {
    for (const s of nav) for (const i of s.items) {
      if (loc.pathname === i.to || loc.pathname.startsWith(i.to + '/')) return i.label
    }
    return 'Beebro'
  })()

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Мобильный топбар */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-neutral-200 h-14 flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)}
                className="w-9 h-9 -ml-1.5 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-700"
                aria-label="Меню">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </button>
        <div className="font-semibold text-[15px] tracking-tight">{currentTitle}</div>
        <div className="w-9 h-9" />
      </header>

      {/* Оверлей */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      )}

      {/* Сайдбар */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-neutral-200 flex flex-col
        transition-transform duration-200 ease-out
        ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:w-60
      `}>
        <div className="h-14 px-5 flex items-center justify-between border-b border-neutral-200">
          <div className="leading-tight">
            <div className="text-[14px] font-semibold tracking-tight">Beebro</div>
            <div className="text-[10px] text-neutral-500">Панель хостинга</div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-neutral-400 hover:text-neutral-900 -mr-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {nav.map((sec) => (
            <div key={sec.section} className="mb-5">
              <div className="px-5 mb-1.5 text-[11px] font-medium text-neutral-400 uppercase tracking-wider">
                {sec.section}
              </div>
              <div className="px-2 space-y-0.5">
                {sec.items.map((n) => {
                  const active = loc.pathname === n.to || loc.pathname.startsWith(n.to + '/')
                  const Icon = n.icon
                  const cls = `group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-colors ${
                    active ? 'bg-neutral-100 text-neutral-900 font-medium'
                           : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  } ${n.disabled ? 'opacity-40 pointer-events-none' : ''}`
                  const iconCls = active ? 'text-neutral-900' : 'text-neutral-400 group-hover:text-neutral-600'
                  if (n.external) {
                    return (
                      <a key={n.to} href={n.external} target="_blank" rel="noreferrer" className={cls}>
                        <Icon className={`shrink-0 ${iconCls}`} />
                        <span className="flex-1">{n.label}</span>
                        <IconExternal className="w-3.5 h-3.5 text-neutral-400" />
                      </a>
                    )
                  }
                  return (
                    <Link key={n.to} to={n.disabled ? '#' : n.to} className={cls}>
                      <Icon className={`shrink-0 ${iconCls}`} />
                      <span className="flex-1">{n.label}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-neutral-200 p-3">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="w-8 h-8 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-700 flex items-center justify-center text-xs font-semibold shrink-0">
              {user?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium text-neutral-900 truncate">{user?.email}</div>
              <div className="text-[10px] text-neutral-500">{user?.role === 'admin' ? 'Администратор' : user?.role}</div>
            </div>
            <button onClick={logout} title="Выйти"
                    className="text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 w-8 h-8 rounded-md flex items-center justify-center transition-colors">
              <IconLogout />
            </button>
          </div>
        </div>
      </aside>

      <main className="lg:pl-60">
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
